import { useEffect, useState } from 'react'
import { useRailInset } from '../hooks/useRailInset'

/**
 * Debug HUD for the keyboard-driven ProbeReferenceSphere rig. Polls the live
 * window globals every 100ms (off the render hot path — same pattern as the
 * BlueprintEditor debug poll) and shows the values the rig is sampling:
 *
 *   • rig position (window.__PROBE_SPHERE_POS__)
 *   • diffuse ambient RGB sampled from the LightProbe (Phase A grid)
 *   • specular reflection-capture pair + blend weight (Phase B)
 *
 * Renders nothing unless the rig is mounted (its position global is present),
 * so it auto-hides outside the debug test. Mounted as a DOM sibling of <Canvas>
 * in App.jsx, like SplatLoadingOverlay.
 */
export default function ProbeReadoutHUD() {
  // Clear the docked Outliner rail, which shares this edge (0 when hidden).
  const railInset = useRailInset()
  const [data, setData] = useState(null)

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof window === 'undefined') return
      const pos = window.__PROBE_SPHERE_POS__
      if (!pos) { setData(null); return } // rig not mounted → hide
      const probe = window.__LIGHT_PROBE__
      const c = probe?.sh?.coefficients?.[0]
      const refl = window.__REFLECTION_CAPTURES__
      setData({
        pos,
        ambient: c ? [c.x, c.y, c.z] : null,
        gridState: window.__LIGHT_PROBE_GRID__?.state ?? 'idle',
        reflState: refl?.state ?? 'idle',
        pair: refl?.activePair ?? null,
        weight: refl?.weight,
      })
    }, 100)
    return () => clearInterval(id)
  }, [])

  if (!data) return null

  const { pos, ambient, gridState, reflState, pair, weight } = data
  // Map the linear-ish ambient SH to a 0..255 swatch (clamped) for a quick
  // visual read alongside the numbers.
  const swatch = ambient
    ? `rgb(${ambient.map(v => Math.max(0, Math.min(255, Math.round(v * 180)))).join(',')})`
    : '#444'

  const row = { display: 'flex', justifyContent: 'space-between', gap: 12 }
  const label = { color: '#8aa' }

  return (
    <div style={{
      // Below the Lighting/Navmesh status chip (now at top:46) so they don't overlap,
      // and right of the docked Outliner rail when it's open (ENG-895).
      position: 'absolute', top: 110, left: 12 + railInset, zIndex: 20,
      font: '11px var(--m-mono)',
      color: '#e6e6e6', background: 'rgba(0,0,0,0.72)',
      border: '1px solid #2a3a3a', borderRadius: 8, padding: '8px 10px',
      minWidth: 230, pointerEvents: 'none', lineHeight: 1.5,
    }}>
      <div style={{ color: '#9fe', marginBottom: 4, fontWeight: 600 }}>
        PROBE RIG — arrows / PageUp·Dn / Shift
      </div>
      <div style={row}>
        <span style={label}>pos</span>
        <span>{pos.map(v => v.toFixed(2)).join(', ')}</span>
      </div>
      <div style={row}>
        <span style={label}>diffuse grid</span>
        <span style={{ color: gridState === 'applied' ? '#7e7' : '#e77' }}>{gridState}</span>
      </div>
      <div style={row}>
        <span style={label}>ambient RGB</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 12, height: 12, borderRadius: 3, background: swatch,
            border: '1px solid #555', display: 'inline-block',
          }} />
          {ambient ? ambient.map(v => v.toFixed(2)).join(' ') : '—'}
        </span>
      </div>
      <div style={row}>
        <span style={label}>reflections</span>
        <span style={{ color: reflState === 'applied' ? '#7e7' : reflState === 'no-cache' ? '#ea7' : '#aaa' }}>{reflState}</span>
      </div>
      {pair && (
        <div style={row}>
          <span style={label}>capture pair</span>
          <span>{pair[0]}↔{pair[1]} w={typeof weight === 'number' ? weight.toFixed(2) : '—'}</span>
        </div>
      )}
    </div>
  )
}
